import { ChangeDetectionStrategy, ChangeDetectorRef, Component, computed, inject, OnInit } from '@angular/core';
import { FormlyModule } from '@ngx-formly/core';
import { MatFormFieldModule } from '@angular/material/form-field';
import { ReactiveFormsModule } from '@angular/forms';
import { MatOption } from '@angular/material/autocomplete';
import { MatSelect } from '@angular/material/select';
import { FormlyErrorComponent } from '../formly-error.component';
import { FieldLabelRowComponent } from './field-label-row.component';
import { FormlyDataCyDirective } from '../../directives/formly-data-cy.directive';
import { ModulesStore } from '../../../features/admin/modules/modules.store';
import { SelectFieldSuffixComponent } from '../select-field-suffix.component';
import { ClearableFieldType } from './clearable-field.type';
import { injectDestroy } from 'ngxtension/inject-destroy';
import { merge, takeUntil } from 'rxjs';

@Component({
  selector: 'app-formly-module-select',
  template: `
    <mat-form-field class="w-full">
      <mat-label>
        <app-field-label-row [label]="props.label" [required]="props.required" [info]="props.info"></app-field-label-row>
      </mat-label>
      <mat-select
        [required]="props.required"
        [formControl]="formControl"
        [formlyAttributes]="field"
        [formlyDataCy]="field">
        @for (option of moduleOptions(); track option.value) {
          <mat-option [value]="option.value">
            {{ option.label }}
          </mat-option>
        }
        @if (moduleOptions().length === 0) {
          <mat-option disabled>Geen modules beschikbaar</mat-option>
        }
      </mat-select>
      <app-select-field-suffix
        matSuffix
        [field]="field"
        [hasValue]="hasClearableValue()"
        [disabled]="isDisabled()"
        [readOnly]="isReadOnly()"
        [ariaLabel]="clearAriaLabel()"
        (clearSelection)="clear()" />
      @if (props.description) {
        <mat-hint>{{ props.description }}</mat-hint>
      }
    </mat-form-field>
    @if (formControl.errors) {
      <app-formly-error [field]="field"></app-formly-error>
    }
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    FormlyModule,
    MatFormFieldModule,
    ReactiveFormsModule,
    MatOption,
    MatSelect,
    FormlyErrorComponent,
    FormlyDataCyDirective,
    SelectFieldSuffixComponent,
    FieldLabelRowComponent,
  ],
  styles: `
    ::ng-deep .mat-mdc-select-arrow {
      opacity: 0 !important;
    }

    mat-form-field:focus,
    mat-form-field:active,
    mat-form-field:focus-within {
      mat-icon {
        color: var(--fuse-primary);
      }
    }
  `,
})
export class FormlyModuleSelectComponent extends ClearableFieldType implements OnInit {
  readonly #modulesStore = inject(ModulesStore);
  readonly #changeDetectorRef = inject(ChangeDetectorRef);
  readonly #destroy$ = injectDestroy();

  moduleOptions = computed(() =>
    this.#modulesStore
      .enabledModules()
      .map(module => ({ label: module.title, value: module.id }))
      .sort((a, b) => a.label.localeCompare(b.label, 'nl', { sensitivity: 'base' }))
  );

  ngOnInit(): void {
    merge(this.formControl.valueChanges, this.formControl.statusChanges)
      .pipe(takeUntil(this.#destroy$))
      .subscribe(() => this.#changeDetectorRef.markForCheck());
  }
}
